import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ApiError } from '@/lib/api';
import { DecisionCard } from '@/pages/cade/DecisionCard';
import { Stat } from '@/pages/cade/Stat';
import { fetchCadeVotedDecision } from '@/services/cade';
import type { CadeDecisionVoteRecord, CadeVote } from '@/services/types';

/**
 * One decision, by its natural key: `/cade/votes/d/:key`.
 *
 * The page a dissent row, a search hit, a ledger row or a case card opens, and
 * an address a reader can cite. The case page draws every decision of a case
 * as a card; this draws ONE, with the votes as minuted laid out in full -- who
 * was written down, as written, with the canonical name beside it, the
 * position, and what the position was recorded against -- plus the collapsed
 * count, so "N rows, M people" is on the face of the page rather than folded
 * away (2026-09-09: a member minuted twice with two positions is one act).
 *
 * Nothing here is a rate. The people who sat on the merits are listed as a
 * set, never as pairs: a pair count on one decision is a number with no
 * denominator.
 */
export function CadeDecisionPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const { key = '' } = useParams();
  const number = new Intl.NumberFormat(i18n.language);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'decision', key],
    queryFn: () => fetchCadeVotedDecision(key),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={3} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    // A malformed or stale key is a not-found state, not a retry: the catch-all
    // would otherwise turn a dead link into the overview.
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4 p-6">
        <BackLink />
        <ErrorMessage
          message={notFound ? t('cade:decision.notFound') : error.message}
          onRetry={notFound ? undefined : () => void refetch()}
        />
      </div>
    );
  }

  const decision = data.decision;

  return (
    <div className="space-y-4 p-6">
      <BackLink />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold tabular-nums text-foreground">
            {t('cade:decision.title', {
              case: data.case_number,
              item: decision.item_number ?? '—',
            })}
          </h1>
          <p className="text-sm text-muted-foreground">
            <Link
              to={`/cade/c/${data.case_key}`}
              className="text-primary underline-offset-4 hover:underline"
            >
              {t('cade:decision.openCase')}
            </Link>
            {data.case_type ? ` · ${data.case_type}` : ''}
            {' · '}
            {t('cade:decision.ofCase', {
              decisions: number.format(data.case_decisions),
              sessions: number.format(data.case_sessions),
            })}
            {data.filing_year !== null
              ? ` · ${t('cade:case.dates.filed', { year: data.filing_year })}`
              : ''}
          </p>
        </div>
        <ExportButton path={`/api/v1/cade/export/votes/d/${encodeURIComponent(key)}`} />
      </header>

      <ExplainerCard i18nKey="cade:decision.explainer" />
      <AsOfStrip asOf={data.as_of} />

      {/* The same card the case page draws, standalone: item, session, ata
          file, relator, unanimity, vista, outcomes, fines, attributed fines,
          parties, citations, the clause and the whole item. */}
      <DecisionCard decision={decision} standalone caseKey={data.case_key} />

      <section className="space-y-3 rounded-lg border bg-card p-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{t('cade:decision.votes.title')}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{t('cade:decision.votes.help')}</p>
        </div>
        <VoteRecordPanel record={data.vote_record} votes={decision.votes} />
        <FieldGuide i18nKey="cade:decision.guide" />
      </section>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation(['cade']);
  return (
    <Link
      to="/cade/votes"
      className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-4 hover:underline"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {t('cade:decision.backToVotes')}
    </Link>
  );
}

/**
 * The votes as minuted, then the collapsed count, then who sat on the merits.
 *
 * Rows are what the minutes say and stay in the table twice when a member was
 * minuted twice; the line above the table says how many people that is.
 */
function VoteRecordPanel({ record, votes }: { record: CadeDecisionVoteRecord; votes: CadeVote[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const collapsed = record.rows > record.people + record.unresolved;

  if (votes.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('cade:decision.votes.none')}</p>;
  }

  return (
    <div className="space-y-3 text-xs">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-vote-record>
        <Stat label={t('cade:decision.votes.rows')} value={number.format(record.rows)} />
        <Stat label={t('cade:decision.votes.people')} value={number.format(record.people)} />
        <Stat label={t('cade:decision.votes.recused')} value={number.format(record.recused)} />
        <Stat
          label={t('cade:decision.votes.unresolved')}
          value={number.format(record.unresolved)}
        />
      </dl>
      {collapsed ? (
        <p className="rounded border border-dashed bg-muted/30 px-2 py-1 text-muted-foreground">
          {t('cade:decision.votes.collapsed', {
            rows: number.format(record.rows),
            people: number.format(record.people),
          })}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-lg border" role="region" aria-label={t('cade:decision.votes.tableLabel')}>
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('cade:decision.votes.asMinuted')}</th>
              <th className="px-3 py-2 font-medium">{t('cade:decision.votes.canonical')}</th>
              <th className="px-3 py-2 font-medium">{t('cade:decision.votes.position')}</th>
              <th className="px-3 py-2 font-medium">{t('cade:decision.votes.target')}</th>
            </tr>
          </thead>
          <tbody>
            {votes.map((vote, i) => (
              <tr key={`${vote.voter ?? ''}-${vote.position}-${i}`} className="border-t align-top">
                <td className="px-3 py-2 text-foreground">{vote.voter ?? '—'}</td>
                <td className="px-3 py-2">
                  {vote.voter_slug && vote.voter_canonical ? (
                    <Link
                      to={`/cade/r/${vote.voter_slug}`}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {vote.voter_canonical}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-foreground">
                  {t(`cade:overview.positions.${vote.position}`, { defaultValue: vote.position })}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{vote.target ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* A set of names, never pairs: who was written down with a position on
          the merits of THIS decision. Recusals are withdrawals, not sides. */}
      {record.on_the_merits.length > 0 ? (
        <p className="text-muted-foreground">
          <span className="font-medium text-foreground">{t('cade:decision.together.title')}: </span>
          {record.on_the_merits.map((member, i) => (
            <span key={member.slug || member.name}>
              {i > 0 ? ', ' : ''}
              {member.slug ? (
                <Link
                  to={`/cade/r/${member.slug}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {member.name}
                </Link>
              ) : (
                member.name
              )}
            </span>
          ))}
          {' · '}
          {t('cade:decision.together.help')}
        </p>
      ) : null}
      <p className="italic text-muted-foreground">{t('cade:case.votesNotAgreement')}</p>
    </div>
  );
}
